import { ExternalLink, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtPrice } from "@/lib/api";

const ROWS = [
  ["Price", (l) => fmtPrice(l.price, l.currency)],
  ["Seller", (l) => l.seller],
  ["Brand", (l) => l.brand],
  ["Part #", (l) => l.part_number],
  ["Condition", (l) => l.condition],
  ["Shipping", (l) => l.shipping],
  ["Fitment", (l) => l.fitment_notes],
];

export const CompareDialog = ({ open, onOpenChange, listings }) => {
  const sorted = [...listings].sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
  const low = sorted[0]?.price;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] bg-card border-border rounded-sm p-0" data-testid="compare-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-display text-3xl uppercase">Price compare <span className="text-primary">({sorted.length})</span></DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto max-h-[70vh] px-6 pb-6">
          <table className="w-full text-sm border border-border" data-testid="compare-table">
            <thead>
              <tr>
                <th className="sticky left-0 bg-card w-28 border-b border-border" />
                {sorted.map((l) => (
                  <th key={l.id} className={`min-w-[200px] p-3 text-left align-top border-b border-l border-border font-semibold ${l.price === low && low != null ? "bg-emerald-500/10" : ""}`}>
                    <span className="line-clamp-3">{l.title}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, get]) => (
                <tr key={label}>
                  <td className="sticky left-0 bg-card p-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">{label}</td>
                  {sorted.map((l) => (
                    <td key={l.id} data-testid={`compare-${label.toLowerCase().replace(/\W/g, "")}-${l.id}`}
                      className={`p-3 align-top border-b border-l border-border ${label === "Price" ? "font-display text-2xl font-bold" : ""} ${label === "Price" && l.price === low && low != null ? "text-emerald-400" : ""} ${label === "Part #" ? "font-mono text-amber-400" : ""}`}>
                      {get(l) || <span className="text-slate-600">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="sticky left-0 bg-card" />
                {sorted.map((l) => (
                  <td key={l.id} className="p-3 border-l border-border">
                    <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`compare-buy-link-${l.id}`}
                      className="inline-flex items-center gap-1.5 bg-primary text-white px-3 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider hover:bg-orange-600">
                      Go to seller <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CompareBar = ({ count, onOpen, onClear }) =>
  count > 0 && (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 rise flex items-center gap-3 bg-[#0B0F17]/90 backdrop-blur-xl border border-primary rounded-sm pl-5 pr-2 py-2 shadow-2xl" data-testid="compare-bar">
      <span className="text-sm"><span className="font-mono text-primary">{count}</span> selected</span>
      <button onClick={onOpen} disabled={count < 2} data-testid="compare-parts-btn"
        className="bg-primary text-white px-4 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider hover:bg-orange-600 disabled:opacity-40">
        {count < 2 ? "Pick 2+ to compare" : "Compare side by side"}
      </button>
      <button onClick={onClear} className="p-2 text-muted-foreground hover:text-white" data-testid="compare-clear-btn"><X className="h-4 w-4" /></button>
    </div>
  );
